/**
 * Primary entity card with expandable detail sections.
 */

import { useState } from "react";
import {
  Building2,
  ChevronDown,
  Users,
  PieChart,
  Shield,
  Link2,
  Scale,
  Tag,
  Info,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { Badge } from "@/ui/primitives/badge";
import type { EntityNode } from "../types";
import { SectionHeader } from "./SectionHeader";
import { ShareholderTable, SecurityTable } from "./tables";
import { MiniEntityCard, RelatedEntitiesList } from "./cards";

export function EntityCard({
  node,
  defaultExpanded = false,
  onDocumentClick,
}: {
  node: EntityNode;
  defaultExpanded?: boolean;
  onDocumentClick?: (documentId: string) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showOverview, setShowOverview] = useState(false);

  // The agent sometimes returns a rich, multi-sentence company profile in
  // `status` (incorporation, registered office, CreditorWatch findings,
  // etc.) rather than a short label. A long string crammed into a Badge
  // reads terribly, so treat anything substantial as a narrative and render
  // it as a collapsible, bulleted overview instead.
  const statusText = node.status?.trim() ?? "";
  const isStatusNarrative = statusText.length > 60;
  const overviewPoints = isStatusNarrative
    ? statusText
        // Split on sentence/clause boundaries (period or semicolon followed
        // by whitespace + a capital letter or digit) so dates, decimals and
        // abbreviations like "s.36(2)" don't get chopped.
        .split(/(?<=[.;])\s+(?=[A-Z0-9])/)
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  // Build summary counts (exclude administrators/liquidators from officers)
  const counts: Array<{ label: string; n: number }> = [
    { label: "officers", n: node.officers.length },
    { label: "shareholders", n: node.shareholders.length },
    { label: "security interests", n: node.securedParties.length },
    { label: "related entities", n: node.relatedEntities.length },
    { label: "professionals", n: node.professionals.length },
  ].filter((c) => c.n > 0);

  const totalEntities = counts.reduce((sum, c) => sum + c.n, 0);

  return (
    <div
      className={cn(
        "w-full overflow-hidden rounded-xl bg-background",
        "shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]",
        "hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]",
        "transition-all duration-200",
        !node.isPrimary && "border border-border",
      )}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full cursor-pointer px-5 py-4 text-left transition-colors hover:bg-muted/20"
      >
        <div className="flex items-start gap-3">
          <Building2
            className={cn(
              "mt-0.5 h-5 w-5 shrink-0",
              node.isPrimary ? "text-primary" : "text-muted-foreground",
            )}
          />
          <div className="min-w-0 flex-1">
            <h3
              className={cn(
                "font-semibold",
                node.isPrimary ? "text-base" : "text-sm",
              )}
            >
              {node.name}
            </h3>

            <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1">
              {node.acn && (
                <span className="font-mono text-[11px] text-muted-foreground">
                  ACN {node.acn}
                </span>
              )}
              {node.abn && (
                <span className="font-mono text-[11px] text-muted-foreground">
                  ABN {node.abn}
                </span>
              )}
              {node.status && !isStatusNarrative && (
                <Badge variant="outline" size="sm">
                  {node.status}
                </Badge>
              )}
              {node.ownership && (
                <Badge variant="secondary" size="sm">
                  {node.ownership}
                </Badge>
              )}
            </div>

            {node.businessNames.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span className="mr-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  Trading as:
                </span>
                {node.businessNames.map((bn) => (
                  <Badge
                    key={bn.name}
                    variant="outline"
                    className="text-[10px] font-normal"
                  >
                    <Tag className="mr-1 h-2.5 w-2.5 opacity-50" />
                    {bn.name}
                  </Badge>
                ))}
              </div>
            )}

            {!isExpanded && totalEntities > 0 && (
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                {counts.map((c) => (
                  <span key={c.label}>
                    {c.n} {c.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          <ChevronDown
            className={cn(
              "mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              isExpanded && "rotate-180",
            )}
          />
        </div>
      </button>

      {/* Company overview — collapsible narrative (kept out of the header
          button to avoid nested interactive elements) */}
      {isStatusNarrative && (
        <div className="border-t px-5 py-3">
          <button
            type="button"
            onClick={() => setShowOverview((v) => !v)}
            className="flex w-full items-center justify-between gap-2 text-left"
          >
            <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <Info className="h-3.5 w-3.5" />
              Overview
            </span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform",
                showOverview && "rotate-180",
              )}
            />
          </button>

          {showOverview && (
            <ul className="mt-3 space-y-1.5">
              {overviewPoints.map((point, i) => (
                <li
                  key={i}
                  className="flex gap-2 text-[13px] leading-relaxed text-muted-foreground"
                >
                  <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-muted-foreground/40" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Detail sections */}
      {isExpanded && totalEntities > 0 && (
        <div className="space-y-6 border-t px-5 py-4">
          {/* Directors & Officers (excludes liquidators/administrators) */}
          {node.officers.length > 0 && (
            <div>
              <SectionHeader
                icon={Users}
                color="text-violet-600 dark:text-violet-400"
                title="Directors & Officers"
                count={node.officers.length}
              />
              <div className="grid grid-cols-2 gap-2">
                {node.officers.map((entity, i) => (
                  <MiniEntityCard
                    key={entity.name + i}
                    entity={entity}
                    onDocumentClick={onDocumentClick}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Shareholders & Trusts — TABLE */}
          {node.shareholders.length > 0 && (
            <div>
              <SectionHeader
                icon={PieChart}
                color="text-sky-600 dark:text-sky-400"
                title="Shareholders & Trusts"
                count={node.shareholders.length}
              />
              <ShareholderTable
                entities={node.shareholders}
                onDocumentClick={onDocumentClick}
              />
            </div>
          )}

          {/* Security Interests — TABLE */}
          {node.securedParties.length > 0 && (
            <div>
              <SectionHeader
                icon={Shield}
                color="text-amber-600 dark:text-amber-400"
                title="Security Interests"
                count={node.securedParties.length}
              />
              <SecurityTable
                entities={node.securedParties}
                onDocumentClick={onDocumentClick}
              />
            </div>
          )}

          {/* Related Entities — FULL WIDTH */}
          {node.relatedEntities.length > 0 && (
            <div>
              <SectionHeader
                icon={Link2}
                color="text-emerald-600 dark:text-emerald-400"
                title="Related Entities"
                count={node.relatedEntities.length}
              />
              <RelatedEntitiesList
                entities={node.relatedEntities}
                onDocumentClick={onDocumentClick}
              />
            </div>
          )}

          {/* Other Professionals */}
          {node.professionals.length > 0 && (
            <div>
              <SectionHeader
                icon={Scale}
                color="text-slate-600 dark:text-slate-400"
                title="Other Professionals"
                count={node.professionals.length}
              />
              <div className="grid grid-cols-2 gap-2">
                {node.professionals.map((entity, i) => (
                  <MiniEntityCard
                    key={entity.name + i}
                    entity={entity}
                    onDocumentClick={onDocumentClick}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!isExpanded && node.children.length > 0 && (
        <div className="border-t bg-muted/10 px-5 py-2 text-[11px] text-muted-foreground">
          {node.children.length} subsidiar
          {node.children.length === 1 ? "y" : "ies"} below
        </div>
      )}
    </div>
  );
}
